/**
 * Public appointment booking page (/book).
 *
 * A single-column, mobile-first form the customer can complete in ~30
 * seconds. No login / OTP — v1 posts straight to
 * `/api/public/appointments/request` which stashes a "pending" booking
 * that ops confirms from the admin queue.
 *
 * Layout: service pick → vehicle + slot → contact + advance → success card.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { PublicPackages, PublicPickup } from '../components/book/BookExtras';
import { BookingCardShare } from '../components/book/BookingCardShare';
import {
  CalendarCheck2, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Phone,
  ShieldCheck, Shield, Sparkles, Droplets, Wrench, Cog, CalendarClock, Sun, Hammer,
  Palette, Layers, Lightbulb, Armchair, SprayCan, AlertCircle,
} from 'lucide-react';

/** Per-service icon + tonal palette + description. Kept muted so the
 *  chip grid reads premium rather than childish. */
const SERVICE_STYLES = {
  'PPF Package':                     { Icon: Shield,        tint: 'teal',    description: 'Full-body Paint Protection Film install with our chosen film & warranty tier.' },
  'Sun Film Package':                { Icon: Sun,           tint: 'amber',   description: 'Heat & UV rejection window film with lifetime rejection warranty.' },
  'Graphene Ceramic Coating Package':{ Icon: Droplets,      tint: 'sky',     description: 'Multi-layer graphene ceramic coating for slick finish + hydrophobic protection.' },
  'Color PPF Application':           { Icon: Palette,       tint: 'violet',  description: 'Colour-change PPF — matte, gloss, satin & custom finishes with full paint protection.' },
  'Body Wrap Application':           { Icon: Layers,        tint: 'rose',    description: 'Full or partial vinyl body wrap — colour change, chrome delete, branding.' },
  'Headlight Upgrade':               { Icon: Lightbulb,     tint: 'amber',   description: 'LED / laser headlight upgrade for brighter throw and modern beam pattern.' },
  'Interior Precut PPF':             { Icon: Armchair,      tint: 'sky',     description: 'Precut PPF for dashboard, door pads & interior trims — scratch-proof, easy to fit.' },
  'Exterior & Interior Detailing':   { Icon: SprayCan,      tint: 'indigo',  description: 'Full exterior wash, decontamination & polish plus interior deep-clean, leather care & dashboard revive.' },
  'Interior Detailing':              { Icon: Sparkles,      tint: 'violet',  description: 'Deep vacuum, leather / fabric conditioning, dashboard revive, odour treatment.' },
  'Body Shop Services':              { Icon: Hammer,        tint: 'rose',    description: 'Dent removal, panel repaint, insurance-grade collision repair.' },
  'Mechanical Servicing':            { Icon: Cog,           tint: 'slate',   description: 'Periodic service, oil / filter change, running diagnostics with report.' },
  'PPF Inspection':                  { Icon: ShieldCheck,   tint: 'emerald', description: 'Bring your PPF for a warranty inspection — edges, top-coat, lift check.' },
  'Annual Maintenance Treatment':    { Icon: CalendarClock, tint: 'zinc',    description: 'Yearly refresh of coating / film — spot-fix, decontamination wash, top-up.' },
};

/** Tailwind class fragments keyed by tint so we get static, purgeable
 *  class strings (no dynamic string concatenation). */
const TINT_CLS = {
  emerald: { chip: 'hover:border-emerald-400 hover:bg-emerald-50/70', badge: 'bg-emerald-100 text-emerald-700', active: 'bg-emerald-600 border-emerald-600' },
  violet:  { chip: 'hover:border-violet-400 hover:bg-violet-50/70',   badge: 'bg-violet-100 text-violet-700',   active: 'bg-violet-600 border-violet-600' },
  indigo:  { chip: 'hover:border-indigo-400 hover:bg-indigo-50/70',   badge: 'bg-indigo-100 text-indigo-700',   active: 'bg-indigo-600 border-indigo-600' },
  sky:     { chip: 'hover:border-sky-400 hover:bg-sky-50/70',         badge: 'bg-sky-100 text-sky-700',         active: 'bg-sky-600 border-sky-600' },
  teal:    { chip: 'hover:border-teal-400 hover:bg-teal-50/70',       badge: 'bg-teal-100 text-teal-700',       active: 'bg-teal-600 border-teal-600' },
  amber:   { chip: 'hover:border-amber-400 hover:bg-amber-50/70',     badge: 'bg-amber-100 text-amber-800',     active: 'bg-amber-600 border-amber-600' },
  slate:   { chip: 'hover:border-slate-400 hover:bg-slate-100/70',    badge: 'bg-slate-200 text-slate-700',     active: 'bg-slate-700 border-slate-700' },
  rose:    { chip: 'hover:border-rose-400 hover:bg-rose-50/70',       badge: 'bg-rose-100 text-rose-700',       active: 'bg-rose-600 border-rose-600' },
  zinc:    { chip: 'hover:border-zinc-400 hover:bg-zinc-100/70',      badge: 'bg-zinc-200 text-zinc-700',       active: 'bg-zinc-700 border-zinc-700' },
};

const inputCls =
  'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/30 transition';

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoInDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const fmtSlot = (s) => s || '';

const BookAppointment = () => {
  const [step, setStep] = useState(1);
  const [meta, setMeta] = useState({ services: [], slot_windows: [], weekly_ppf_cap: 4, ppf_capped_services: [] });
  const [slots, setSlots] = useState([]);
  const [dayInfo, setDayInfo] = useState({ closed: false, blocked_windows: [], weekly_ppf: null });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [err, setErr] = useState('');
  // Vehicle Make/Model auto-suggest — pulled once at mount from the
  // same curated Indian catalog billing uses.
  const [vehicleCatalog, setVehicleCatalog] = useState({ makes: [], models_by_make: {} });
  const [form, setForm] = useState({
    service_types: [],
    customer: { prefix: '', name: '', phone: '', email: '' },
    vehicle: { registration_number: '', make: '', model: '', year: '', km: '' },
    slot_date: isoInDays(1),
    // moved to the first open day once the rules load (see effect below)
    slot_window: '',
    notes: '',
    advance_amount: 0,
    package_ids: [],
    pickup: { required: false, address: '', time: '' },
  });
  const [packages, setPackages] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (k, patch) => setForm((f) => ({ ...f, [k]: { ...f[k], ...patch } }));

  // Bootstrap services + slot windows
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/appointments/services`);
        setMeta(data);
        const open = data.open_days || [0, 1, 2, 3, 4, 5];
        let n = 1;
        while (n < 8 && !open.includes((new Date(`${isoInDays(n)}T00:00:00`).getDay() + 6) % 7)) n += 1;
        setForm((f) => (f.slot_date === isoInDays(1) ? { ...f, slot_date: isoInDays(n) } : f));
      } catch (e) { setErr('Could not load booking form. Please retry.'); }
    })();
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/appointments/packages`).then(({ data }) => setPackages(data || [])).catch(() => setPackages([]));
  }, []);
  const togglePackage = (id) => setForm((f) => ({ ...f, package_ids: f.package_ids.includes(id) ? f.package_ids.filter((x) => x !== id) : [...f.package_ids, id] }));

  // Vehicle catalog — never blocks the form; fails silent if endpoint is
  // slow so the user can still type freely.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/appointments/vehicle-catalog`);
        setVehicleCatalog(data || { makes: [], models_by_make: {} });
      } catch (e) { /* silent */ }
    })();
  }, []);

  const loadSlots = useCallback(async (date, serviceType) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const params = new URLSearchParams({ date });
      if (serviceType) params.set('service_type', serviceType);
      const { data } = await axios.get(`${API}/public/appointments/slots?${params.toString()}`);
      setSlots(data.slots || []);
      setDayInfo({
        closed: !!data.closed,
        blocked_windows: data.blocked_windows || [],
        weekly_ppf: data.weekly_ppf || null,
      });
    } catch (e) { setSlots([]); setDayInfo({ closed: false, blocked_windows: [], weekly_ppf: null }); }
    finally { setLoadingSlots(false); }
  }, []);

  const svcKey = form.service_types.join(',');
  useEffect(() => { loadSlots(form.slot_date, svcKey); }, [form.slot_date, svcKey, loadSlots]);

  // When service or date changes, drop the picked slot if it's no longer
  // available (e.g. switched to a PPF service that's now capped).
  useEffect(() => {
    if (!form.slot_window) return;
    const s = slots.find((x) => x.window === form.slot_window);
    if (s && s.remaining <= 0) setForm((f) => ({ ...f, slot_window: '' }));
  }, [slots, form.slot_window]);

  const step1Ok = form.service_types.length > 0;
  const step2Ok = !!form.vehicle.registration_number && !!form.slot_window;
  const step3Ok = !!form.customer.name?.trim() && /^\d{10}$/.test((form.customer.phone || '').replace(/\D/g, '')) && (!form.pickup.required || !!form.pickup.address.trim());

  const chooseSlot = (win) => {
    const slot = slots.find((s) => s.window === win);
    if (slot && slot.remaining <= 0) return;
    set('slot_window', win);
  };

  const submit = async () => {
    setErr('');
    setSubmitting(true);
    try {
      const payload = {
        service_types: form.service_types,
        customer: {
          prefix: form.customer.prefix || '',
          name: form.customer.name.trim(),
          phone: (form.customer.phone || '').replace(/\D/g, ''),
          email: form.customer.email?.trim() || '',
        },
        vehicle: {
          registration_number: (form.vehicle.registration_number || '').toUpperCase().trim(),
          make: form.vehicle.make?.trim() || '',
          model: form.vehicle.model?.trim() || '',
          year: form.vehicle.year ? Number(form.vehicle.year) : null,
          km: form.vehicle.km ? Number(form.vehicle.km) : null,
        },
        slot_date: form.slot_date,
        slot_window: form.slot_window,
        notes: (form.notes || '').trim(),
        advance_amount: Number(form.advance_amount || 0),
        package_ids: form.package_ids,
        pickup: form.pickup,
      };
      const { data } = await axios.post(`${API}/public/appointments/request`, payload);
      setConfirmed(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Sorry, we couldn’t save your booking. Please try again.');
    } finally { setSubmitting(false); }
  };

  const dateOptions = useMemo(() => {
    const opts = [];
    for (let i = 1; i <= 14; i += 1) opts.push(isoInDays(i));
    return opts;
  }, []);

  // ---------------- Confirmed card ----------------
  if (confirmed) {
    return (
      <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center pt-24 pb-16 px-4" data-testid="book-confirmed">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-emerald-100 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="text-emerald-600" size={30} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Booking Received</h1>
          <p className="text-sm text-slate-500 mb-5">We’ll call you within 2 working hours to confirm.</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Reference</span><span className="font-mono font-bold text-slate-900" data-testid="confirmed-ref">{confirmed.ref_no}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Service</span><span className="font-semibold text-slate-800">{confirmed.service_type}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-semibold text-slate-800">{confirmed.slot_date}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Slot</span><span className="font-semibold text-slate-800">{fmtSlot(confirmed.slot_window)}</span></div>
            {(confirmed.packages || []).length > 0 && (
              <div className="flex justify-between gap-3" data-testid="confirmed-packages"><span className="text-slate-500">Packages</span><span className="text-right font-semibold text-slate-800">{confirmed.packages.map((p) => p.name).join(', ')}<span className="block text-xs font-normal text-slate-500">Estimated ₹ {Number(confirmed.packages_total || 0).toLocaleString('en-IN')}</span></span></div>
            )}
            {confirmed.pickup?.required && (
              <div className="flex justify-between gap-3" data-testid="confirmed-pickup"><span className="text-slate-500">Pickup</span><span className="text-right font-semibold text-slate-800">{[confirmed.pickup.time, confirmed.pickup.address].filter(Boolean).join(' · ')}<span className="block text-xs font-normal text-slate-500">We&rsquo;ll call to confirm</span></span></div>
            )}
          </div>
          {confirmed.card_token && <BookingCardShare confirmed={confirmed} />}
          <a href="/" className="mt-6 inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition" data-testid="confirmed-home">Back to home</a>
        </div>
      </div>
    );
  }

  // ---------------- Form ----------------
  return (
    <div className="min-h-[80vh] bg-slate-50 pt-32 pb-16 px-4" data-testid="book-appointment">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Book your appointment</h1>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
            Pick a service, choose a slot, we&rsquo;ll take it from there. No login needed.
          </p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="book-steps">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-2 w-10 rounded-full transition ${n <= step ? 'bg-teal-500' : 'bg-slate-200'}`} />
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
          {/* Step 1 — service */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-service">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">Which services do you need? <span className="font-normal text-slate-500">You can pick more than one.</span></label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {(meta.services || []).map((s) => {
                    const style = SERVICE_STYLES[s] || {};
                    const { Icon = ShieldCheck, tint = 'zinc', description = '' } = style;
                    const cls = TINT_CLS[tint] || TINT_CLS.zinc;
                    const active = form.service_types.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        aria-pressed={active}
                        onClick={() => set('service_types', active ? form.service_types.filter((x) => x !== s) : [...form.service_types, s])}
                        className={`group flex items-start gap-3 text-left px-4 py-3.5 rounded-xl border transition ${
                          active
                            ? `text-white border-transparent shadow-sm ${cls.active}`
                            : `bg-white text-slate-700 border-slate-200 ${cls.chip}`
                        }`}
                        data-testid={`service-${s.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition mt-0.5 ${
                          active ? 'bg-white/20 text-white' : cls.badge
                        }`}>
                          <Icon size={17} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold leading-tight">{s}</span>
                          {description && (
                            <span className={`block text-[11px] mt-1 leading-snug ${active ? 'text-white/80' : 'text-slate-500'}`}>
                              {description}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.service_types.length > 0 && <PublicPackages packages={packages} picked={form.package_ids} onToggle={togglePackage} />}
              <div className="pt-1 flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={!step1Ok}
                  className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition"
                  data-testid="btn-next-1"
                >Continue <ChevronRight size={16} /></button>
              </div>
            </div>
          )}

          {/* Step 2 — vehicle + slot */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-vehicle-slot">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Your vehicle</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    className={inputCls}
                    placeholder="Reg. no (e.g. KA01AB1234)"
                    maxLength={10}
                    value={form.vehicle.registration_number}
                    onChange={(e) => setNested('vehicle', { registration_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                    data-testid="input-reg"
                  />
                  <input
                    className={inputCls}
                    placeholder="Make (e.g. Toyota)"
                    list="book-make-list"
                    autoComplete="off"
                    value={form.vehicle.make}
                    onChange={(e) => {
                      const nextMake = e.target.value;
                      // Blank out the model when the make changes to a
                      // different value so we don't end up with a Ford Innova.
                      setNested('vehicle', {
                        make: nextMake,
                        ...(nextMake !== form.vehicle.make ? { model: '' } : {}),
                      });
                    }}
                    data-testid="input-make"
                  />
                  <datalist id="book-make-list">
                    {(vehicleCatalog.makes || []).map((m) => <option key={m} value={m} />)}
                  </datalist>
                  <input
                    className={inputCls}
                    placeholder="Model (e.g. Innova)"
                    list="book-model-list"
                    autoComplete="off"
                    value={form.vehicle.model}
                    onChange={(e) => setNested('vehicle', { model: e.target.value })}
                    data-testid="input-model"
                  />
                  <datalist id="book-model-list">
                    {((vehicleCatalog.models_by_make || {})[form.vehicle.make] || []).map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                  <input
                    className={inputCls}
                    placeholder="Year (optional)"
                    inputMode="numeric"
                    value={form.vehicle.year}
                    onChange={(e) => setNested('vehicle', { year: e.target.value })}
                    data-testid="input-year"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Preferred date</label>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {dateOptions.map((d) => {
                    const dObj = new Date(d + 'T00:00:00');
                    const isSunday = !(meta.open_days || [0, 1, 2, 3, 4, 5]).includes((dObj.getDay() + 6) % 7);
                    const active = form.slot_date === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={isSunday}
                        onClick={() => set('slot_date', d)}
                        className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border text-xs font-semibold transition ${
                          isSunday
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through'
                            : active
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-teal-400'
                        }`}
                        title={isSunday ? 'Closed this day' : ''}
                        data-testid={`date-${d}`}
                      >
                        <span>{dObj.toLocaleString('en-IN', { weekday: 'short' })}</span>
                        <span className="text-base">{dObj.getDate()}</span>
                        <span className="text-[10px] opacity-80">{dObj.toLocaleString('en-IN', { month: 'short' })}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2 gap-2">
                  <label className="block text-sm font-semibold text-slate-800">Slot</label>
                  <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-500" data-testid="slot-legend">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Available</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Filling</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Booked</span>
                  </div>
                </div>

                {/* Weekly PPF banner — shown only when the customer picked
                 *  a PPF-family service and we know the weekly usage. */}
                {form.service_types.some((s) => (meta.ppf_capped_services || []).includes(s)) && dayInfo.weekly_ppf?.cap > 0 && (
                  <div
                    className={`mb-2 rounded-lg border px-3 py-2 text-[11px] font-semibold flex items-center gap-2 ${
                      dayInfo.weekly_ppf.remaining <= 0
                        ? 'bg-rose-50 border-rose-200 text-rose-700'
                        : dayInfo.weekly_ppf.remaining <= 1
                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    }`}
                    data-testid="ppf-cap-banner"
                  >
                    <AlertCircle size={13} />
                    {dayInfo.weekly_ppf.remaining <= 0
                      ? `PPF bookings are full for this week (${dayInfo.weekly_ppf.used}/${dayInfo.weekly_ppf.cap}). Please pick a date next week.`
                      : `${dayInfo.weekly_ppf.remaining} of ${dayInfo.weekly_ppf.cap} PPF slots left this week.`}
                  </div>
                )}

                {dayInfo.closed && (
                  <div className="mb-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-600 px-3 py-2 text-[11px] font-semibold flex items-center gap-2" data-testid="closed-banner">
                    <AlertCircle size={13} /> We&rsquo;re closed on this day. Please pick another date.
                  </div>
                )}
                {!dayInfo.closed && (dayInfo.blocked_windows || []).length > 0 && (
                  <div className="mb-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-600 px-3 py-2 text-[11px] font-semibold flex items-center gap-2" data-testid="friday-block-banner">
                    <AlertCircle size={13} /> Workshop hold today: {(dayInfo.blocked_windows || []).join(', ')} not available.
                  </div>
                )}

                {loadingSlots ? (
                  <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading slots…</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {slots.map((s) => {
                      const active = form.slot_window === s.window;
                      const full = s.remaining <= 0;
                      const reason = s.unavailable_reason;
                      // Tone the base pill by fill level so customers can
                      // spot filling / booked slots without reading text.
                      const filling = !full && s.remaining <= Math.max(1, Math.floor((s.used + s.remaining) * 0.25));
                      const tone = full
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : filling
                          ? 'bg-amber-50 text-amber-900 border-amber-200 hover:border-amber-400'
                          : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:border-emerald-400';
                      const dot = full ? 'bg-slate-400' : filling ? 'bg-amber-500' : 'bg-emerald-500';
                      const cap = s.used + s.remaining;
                      const captionForReason = () => {
                        if (reason === 'closed') return 'Closed';
                        if (reason === 'blocked') return 'Held';
                        if (reason === 'ppf_weekly_cap') return 'PPF weekly cap';
                        return full ? 'Fully booked' : `${s.used}/${cap} booked · ${s.remaining} left`;
                      };
                      return (
                        <button
                          key={s.window}
                          type="button"
                          disabled={full}
                          onClick={() => chooseSlot(s.window)}
                          className={`relative text-left px-3 py-2.5 rounded-lg border transition ${
                            active ? 'bg-teal-600 text-white border-teal-600 shadow-sm' : tone
                          }`}
                          data-testid={`slot-${s.window}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-white' : dot}`} />
                            <span className="text-xs font-bold tabular-nums">{fmtSlot(s.window)}</span>
                          </div>
                          <div className={`text-[10px] font-semibold mt-0.5 tabular-nums ${active ? 'text-white/80' : 'text-slate-500'}`}>
                            {captionForReason()}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="pt-1 flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm font-semibold px-3 py-2.5 rounded-lg transition"
                  data-testid="btn-back-2"
                ><ChevronLeft size={16} /> Back</button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!step2Ok}
                  className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition"
                  data-testid="btn-next-2"
                >Continue <ChevronRight size={16} /></button>
              </div>
            </div>
          )}

          {/* Step 3 — contact + advance */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-contact">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Your contact</label>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex gap-2">
                  <select className={`${inputCls} !w-24 shrink-0`} value={form.customer.prefix} onChange={(e) => setNested('customer', { prefix: e.target.value })} aria-label="Title" data-testid="input-prefix">
                    <option value="">Title</option>
                    {['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'M/s'].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input
                    className={inputCls}
                    placeholder="Full name"
                    value={form.customer.name}
                    onChange={(e) => setNested('customer', { name: e.target.value })}
                    data-testid="input-name"
                  />
                  </div>
                  <input
                    className={inputCls}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.customer.phone}
                    onChange={(e) => setNested('customer', { phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    data-testid="input-phone"
                  />
                  <input
                    className={inputCls}
                    placeholder="Email (optional)"
                    value={form.customer.email}
                    onChange={(e) => setNested('customer', { email: e.target.value })}
                    data-testid="input-email"
                  />
                </div>
              </div>

              <PublicPickup pickup={form.pickup} inputCls={inputCls} onChange={(patch) => setNested('pickup', patch)} />

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Anything we should know? (optional)</label>
                <textarea
                  rows={3}
                  className={inputCls}
                  placeholder="e.g. Rear bumper scratch, coating peeling near bonnet…"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  data-testid="input-notes"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-amber-600 flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-800">Reserve the slot with an advance (optional)</div>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Pay a small advance to lock this slot. Online payment goes live soon —
                      until then we’ll call you to confirm the mode.
                    </p>
                    <input
                      type="number"
                      min={0}
                      step={100}
                      className={`${inputCls} mt-2 max-w-[180px]`}
                      placeholder="₹ 0"
                      value={form.advance_amount || ''}
                      onChange={(e) => set('advance_amount', e.target.value)}
                      data-testid="input-advance"
                    />
                  </div>
                </div>
              </div>

              {err && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-xs" data-testid="book-error">{err}</div>
              )}

              <div className="pt-1 flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm font-semibold px-3 py-2.5 rounded-lg transition"
                  data-testid="btn-back-3"
                ><ChevronLeft size={16} /> Back</button>
                <button
                  onClick={submit}
                  disabled={!step3Ok || submitting}
                  className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition"
                  data-testid="btn-submit"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck2 size={14} />}
                  Confirm booking
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Need help? Call us at <a href="tel:+919916120561" className="text-teal-600 font-semibold"><Phone size={10} className="inline" /> +91 99161 20561</a>
        </p>
      </div>
    </div>
  );
};

export default BookAppointment;
